import { useCallback, useRef, useState } from 'react';
import type { TextInput } from 'react-native';

interface Selection {
  start: number;
  end: number;
}

interface UseFocusAndInsertResult {
  /** Ref to attach to the TextInput / TextArea. */
  inputRef: React.MutableRefObject<TextInput | null>;
  /** Current cursor / selection range — keep in sync via onSelectionChange. */
  selection: Selection;
  /** Update the tracked selection (call from onSelectionChange). */
  setSelection: (next: Selection) => void;
  /**
   * Restore focus to the input AND place the cursor at the given offset.
   * Use after a toolbar action that briefly stole focus — plain
   * `.focus()` on RN-web puts the cursor at the end, which is wrong if
   * the action inserted text mid-document.
   */
  focusAtCursor: (cursor: number) => void;
  /**
   * Insert `text` at the current selection, replacing any non-zero
   * selection. Returns the next value (caller is responsible for the
   * controlled-input update — usually `setDraft(next)` then
   * `onChangeText(next)`). Re-focuses + places cursor at end of insert.
   */
  insertAtSelection: (current: string, text: string) => { next: string; cursor: number };
}

/**
 * Single-textarea + toolbar focus pattern, extracted from
 * `apps/mobile/src/components/itinerary/NotesEditorBlock.tsx` per the
 * Phase 5/6 audit. Anything that pairs a `TextInput` / `TextArea` with
 * a `Toolbar` of insert buttons (markdown editor, bug-report description,
 * future composer surfaces) can reuse the focus-restore logic instead
 * of hand-rolling `requestAnimationFrame(() => node.focus())`.
 *
 * Web-specific gotcha the hook handles: `inputRef.current.focus()` on
 * RN-web's `<textarea>` snaps the cursor to the end. `setNativeProps`
 * with a `selection` range translates to `setSelectionRange(start, end)`
 * on web and to the equivalent on native, putting the cursor where the
 * toolbar action wanted it.
 *
 * Usage:
 *
 *   const { inputRef, selection, setSelection, focusAtCursor, insertAtSelection } =
 *     useFocusAndInsert();
 *
 *   <TextArea
 *     ref={inputRef}
 *     value={draft}
 *     onChangeText={setDraft}
 *     onSelectionChange={(e) => setSelection(e.nativeEvent.selection)}
 *   />
 *
 *   <Toolbar
 *     actions={[
 *       { id: 'bold', iconName: 'format-bold', label: 'Bold',
 *         onPress: () => {
 *           const { next, cursor } = insertAtSelection(draft, '**bold**');
 *           setDraft(next);
 *           focusAtCursor(cursor);
 *         } },
 *     ]}
 *   />
 */
export function useFocusAndInsert(): UseFocusAndInsertResult {
  const inputRef = useRef<TextInput | null>(null);
  const [selection, setSelection] = useState<Selection>({ start: 0, end: 0 });

  const focusAtCursor = useCallback((cursor: number) => {
    requestAnimationFrame(() => {
      const node = inputRef.current as unknown as {
        focus?: (() => void) | undefined;
        setNativeProps?: ((props: { selection?: Selection }) => void) | undefined;
      } | null;
      if (!node) return;
      node.focus?.();
      node.setNativeProps?.({ selection: { start: cursor, end: cursor } });
    });
  }, []);

  const insertAtSelection = useCallback(
    (current: string, text: string): { next: string; cursor: number } => {
      const { start, end } = selection;
      const before = current.slice(0, start);
      const after = current.slice(end);
      const next = `${before}${text}${after}`;
      const cursor = start + text.length;
      setSelection({ start: cursor, end: cursor });
      return { next, cursor };
    },
    [selection],
  );

  return { inputRef, selection, setSelection, focusAtCursor, insertAtSelection };
}
